/**
 * Delivery route ordering — the order a delivery boy walks the round.
 *
 *   area order -> area name -> customer position in that area -> customer name
 *
 * Customers with no area always come last. Everything defaults to 0, so a vendor who has never
 * set a route still gets a plain alphabetical list.
 *
 * Mirrors the backend ordering in app/routes/transaction_routes.py (daily-ops) and
 * app/route_order.py. Keep the two in step, or the Daily page and the Customers/Dues sort
 * options will disagree about who comes first.
 */

// The screens carry customers in different shapes: the Customers list uses f_area_id /
// f_sort_order / f_name, the Dues list uses area_id / sort_order / customer_name.
const DEFAULT_KEYS = { areaKey: 'f_area_id', orderKey: 'f_sort_order', nameKey: 'f_name' };

/**
 * Build a comparator for customers in delivery route order.
 * @param areas list from getBusinessAreas (needs f_id, f_sort_order, f_name)
 * @param keys  field names on the customer objects being sorted
 */
export function makeRouteComparator(areas = [], keys = {}) {
    const { areaKey, orderKey, nameKey } = { ...DEFAULT_KEYS, ...keys };
    const byId = new Map((areas || []).map(a => [a.f_id, a]));

    const areaRank = (c) => {
        const a = byId.get(c[areaKey]);
        // No area (or an area we can't see) sorts after every real one.
        return a ? [a.f_sort_order ?? 0, a.f_name || ''] : [Number.MAX_SAFE_INTEGER, ''];
    };

    return (x, y) => {
        const [ax, anx] = areaRank(x);
        const [ay, any] = areaRank(y);
        if (ax !== ay) return ax - ay;
        if (anx !== any) return anx.localeCompare(any);
        const ox = x[orderKey] ?? 0;
        const oy = y[orderKey] ?? 0;
        if (ox !== oy) return ox - oy;
        return (x[nameKey] || '').localeCompare(y[nameKey] || '');
    };
}

/** Areas in the order the round covers them. */
export function sortAreasByRoute(areas = []) {
    return [...areas].sort(
        (a, b) => (a.f_sort_order ?? 0) - (b.f_sort_order ?? 0)
            || (a.f_name || '').localeCompare(b.f_name || '')
    );
}

/** Customers within a single area, in delivery order. */
export function sortCustomersInArea(customers = [], keys = {}) {
    const { orderKey, nameKey } = { ...DEFAULT_KEYS, ...keys };
    return [...customers].sort(
        (a, b) => (a[orderKey] ?? 0) - (b[orderKey] ?? 0)
            || (a[nameKey] || '').localeCompare(b[nameKey] || '')
    );
}
